import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { trainerProfileApi } from '../services/api';
import type { TrainerProfile } from '../services/api';
import { User, Award, Briefcase, Upload, Plus, X } from 'lucide-react';

interface ProfileFormData {
  bio: string;
  certifications: string[];
  specialties: string[];
  years_experience: number;
}

export function Profile() {
  const queryClient = useQueryClient();
  const [certificationInput, setCertificationInput] = useState('');
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [certifications, setCertifications] = useState<string[]>([]);
  const [specialties, setSpecialties] = useState<string[]>([]);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<ProfileFormData>();

  // Fetch existing profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ['trainerProfile'],
    queryFn: async () => {
      try {
        const response = await trainerProfileApi.get();
        return response.data;
      } catch (error: any) {
        if (error.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });

  // Initialize form with existing profile data
  useEffect(() => {
    if (profile) {
      setValue('bio', profile.bio);
      setValue('years_experience', profile.years_experience);
      setCertifications(profile.certifications || []);
      setSpecialties(profile.specialties || []);
    }
  }, [profile, setValue]);

  // Create profile mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<TrainerProfile>) => trainerProfileApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainerProfile'] });
    },
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<TrainerProfile>) => trainerProfileApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainerProfile'] });
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    const profileData = {
      ...data,
      certifications,
      specialties,
    };

    if (profile) {
      updateMutation.mutate(profileData);
    } else {
      createMutation.mutate(profileData);
    }
  };

  const addCertification = () => {
    if (certificationInput.trim() && !certifications.includes(certificationInput.trim())) {
      setCertifications([...certifications, certificationInput.trim()]);
      setCertificationInput('');
    }
  };

  const removeCertification = (cert: string) => {
    setCertifications(certifications.filter(c => c !== cert));
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !specialties.includes(specialtyInput.trim())) {
      setSpecialties([...specialties, specialtyInput.trim()]);
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (spec: string) => {
    setSpecialties(specialties.filter(s => s !== spec));
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </Layout>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;
  const success = createMutation.isSuccess || updateMutation.isSuccess;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Trainer Profile</h1>
          <p className="mt-2 text-gray-600">
            Manage your professional profile and credentials
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {(error as any).message || 'An error occurred while saving your profile'}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            Profile {profile ? 'updated' : 'created'} successfully!
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Bio Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <User className="w-5 h-5 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Bio</h2>
            </div>
            
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                Professional Bio
              </label>
              <textarea
                id="bio"
                rows={6}
                {...register('bio', {
                  required: 'Bio is required',
                  maxLength: { value: 1000, message: 'Bio must be less than 1000 characters' }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Tell clients about your experience, training philosophy, and what makes you unique..."
              />
              {errors.bio && (
                <p className="mt-1 text-sm text-red-600">{errors.bio.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Maximum 1000 characters
              </p>
            </div>
          </div>

          {/* Years of Experience */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Briefcase className="w-5 h-5 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Experience</h2>
            </div>
            
            <div>
              <label htmlFor="years_experience" className="block text-sm font-medium text-gray-700">
                Years of Experience
              </label>
              <input
                id="years_experience"
                type="number"
                min="0"
                {...register('years_experience', {
                  required: 'Years of experience is required',
                  min: { value: 0, message: 'Years of experience must be non-negative' },
                  valueAsNumber: true
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="0"
              />
              {errors.years_experience && (
                <p className="mt-1 text-sm text-red-600">{errors.years_experience.message}</p>
              )}
            </div>
          </div>

          {/* Certifications Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Award className="w-5 h-5 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Certifications</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={certificationInput}
                  onChange={(e) => setCertificationInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="e.g., NASM Certified Personal Trainer"
                />
                <button
                  type="button"
                  onClick={addCertification}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {certifications.length > 0 && (
                <div className="space-y-2">
                  {certifications.map((cert, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-md"
                    >
                      <span className="text-sm text-gray-700">{cert}</span>
                      <button
                        type="button"
                        onClick={() => removeCertification(cert)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Specialties Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Award className="w-5 h-5 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Specialties</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="e.g., Strength Training, Weight Loss, Sports Performance"
                />
                <button
                  type="button"
                  onClick={addSpecialty}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {specialties.length > 0 && (
                <div className="space-y-2">
                  {specialties.map((spec, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-md"
                    >
                      <span className="text-sm text-gray-700">{spec}</span>
                      <button
                        type="button"
                        onClick={() => removeSpecialty(spec)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Profile Photo Section (Placeholder) */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Upload className="w-5 h-5 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Profile Photo</h2>
            </div>
            
            <div className="text-center border-2 border-dashed border-gray-300 rounded-lg p-6">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                Profile photo upload coming soon
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
